import React, {useState} from "react";
import {Col} from "react-bootstrap";
import PropTypes from "prop-types";
import {GSPSIcon} from "Icons/GSPSIcon";
import Modal from "react-bootstrap/Modal";
import {Button} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import {makePatchRequest} from "Services/ServicesUtils";
import {insertNotification} from "Utils/NotificationsUtils";
import {API_AUDITS} from "Models/Audit/AuditEndpoints";
import {AnnotationType} from "../../Models/Audit/AuditConstants";
import {convertToEst, convertToEstDate} from "../../Services/TimeZonesUtils";

function getMediaIconName(mediaType) {
    if (mediaType === "image") {
        return "CardImage";
    }
    return "CameraVideo";
}

function getMediaViewer(
    isPreview,
    isSmallSizeNoNotes,
    mediaType,
    src,
    maxWidthPx,
    maxHeightPx,
    includeControls = false,
    mediaObject,
    isFullViewer = false,
) {
    if (typeof src === "object") {
        src = URL.createObjectURL(src);
    }

    const containerStyle = {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        padding: "1rem",
    };

    let mediaStyle = {
        maxWidth: "100%",
        maxHeight: "80vh",
        objectFit: "cover",
        backgroundColor: "white",
        borderRadius: "5px",
        boxShadow: "0px 0px 8px rgba(0, 0, 0, 0.2)",
        overflow: "hidden",
        height: "250px",
        width: "250px",
        objectPosition: "center",
    };

    if (isSmallSizeNoNotes) {
        mediaStyle = {
            maxWidth: "100%",
            maxHeight: "80vh",
            objectFit: "contain",
            backgroundColor: "white",
            borderRadius: "5px",
            boxShadow: "0px 0px 8px rgba(0, 0, 0, 0.2)",
            height: "70px",
            width: "100px",
            overflow: "hidden",
        };
    }

    if (isPreview) {
        mediaStyle = {
            maxWidth: "100%",
            maxHeight: "80vh",
            objectFit: "contain",
            backgroundColor: "white",
            borderRadius: "5px",
            boxShadow: "0px 0px 8px rgba(0, 0, 0, 0.2)",
            overflow: "hidden",
        };
    }

    function renderCreationDate() {
        return <>
            {isFullViewer || isPreview ?
                <div style={{fontSize: "12px"}}>
                    {mediaObject && "Created on: " + convertToEst(mediaObject.created_at)}
                </div> :
                <div style={{fontSize: "8px"}}>
                    {mediaObject && convertToEstDate(mediaObject.created_at)}
                </div>
            }
        </>;
    }

    if (mediaType === "image") {
        return (
            <div className={"d-flex flex-column align-items-center justify-content-center"}>
                <div style={containerStyle}>
                    <img src={src} style={mediaStyle} alt="Preview" />
                </div>
                {renderCreationDate()}
            </div>
        );
    } else if (mediaType === "video") {
        return (
            <div className={"d-flex flex-column align-items-center justify-content-center"}>
                <div style={containerStyle}>
                    <video
                        src={src}
                        controls={includeControls}
                        style={mediaStyle}
                        playsInline={true} preload="metadata" onLoadedMetadata={(e) => {
                            e.target.currentTime = 0.1;
                        }}>
                    </video>
                </div>
                {renderCreationDate()}
            </div>
        );
    }
    console.warn(`Unknown Media type ${mediaType} received in MediaSelector component`);
    return <></>;
}

export const GSPSMediaSelector = ({
    isSmallSizeNoNotes,
    controlSize,
    imageContainerSizePx,
    imageContainerHeightSizePx,
    notes,
    currentImageSrc,
    mediaType,
    media,
    elementType,
    onNotesSaved,
    isViewOnly = false,
    mediaObject = null,
    isFullViewer = false,
}) => {
    const [isPreviewDisplayed, setIsPreviewDisplayed] = useState(false);
    const [mediaNotes, setMediaNotes] = useState(notes);

    const handleClose = () => setIsPreviewDisplayed(false);
    const handleShow = () => setIsPreviewDisplayed(true);

    const onSaveNotes = () => {
        if (media.id === undefined) {
            onNotesSaved(true, mediaNotes);
            handleClose();
            return;
        }

        const body = {
            "notes": mediaNotes,
            "media_type": mediaType,
            "element_type": elementType, // issue, area, line
        };

        makePatchRequest(`${API_AUDITS.mediaData(media.id)}`, body)
            .then((jsonRes) => {
                insertNotification("Success",
                    `${mediaType} has been updated ..`,
                    "success");
                handleClose();
                onNotesSaved(false, mediaNotes);

                return jsonRes;
            }).catch((error) => {
                error.then((errorMessage) => {
                    const failureReason = JSON.stringify(errorMessage);
                    insertNotification("Error", `can't update ${failureReason}`, "error");
                });
            });
    };

    function renderNotesInput() {
        return <>
            {<div style={{display: "flex", justifyContent: "center", marginTop: "8px"}}>
                <div style={{display: "flex", alignItems: "flex-end"}}>
                    <div style={{display: "flex", flexDirection: "column"}}>
                        <label style={{fontSize: "12px", marginBottom: "4px"}}>
                            Notes:
                        </label>
                        <textarea
                            value={mediaNotes}
                            onChange={(e) => setMediaNotes(e.target.value)}
                            placeholder="Enter notes..."
                            style={{
                                width: "250px",
                                height: "32px",
                                fontSize: "12px",
                                padding: "4px",
                                borderRadius: "4px",
                                border: "1px solid #ccc",
                            }}
                        />
                    </div>
                    <Button
                        className="cta-btn"
                        onClick={onSaveNotes}
                        variant={"outlined"}
                        startIcon={<SaveIcon />}
                        style={{
                            marginLeft: "8px",
                            height: "32px",
                        }}
                    >
                        Save
                    </Button>
                </div>
            </div>}
        </>;
    }

    return <>
        <Col sm={controlSize} className="position-relative">
            <Modal className="modal show" id="exampleModal" role="dialog"
                backdrop="static"
                onHide={handleClose}
                show={isPreviewDisplayed}
                centered={true}
            >
                <Modal.Header closeButton={true}><h5>Preview</h5></Modal.Header>
                <Modal.Body>
                    {getMediaViewer(true,
                        isSmallSizeNoNotes,
                        mediaType,
                        currentImageSrc,
                        450,
                        280,
                        true,
                        mediaObject,
                        isFullViewer)}
                    {!isViewOnly && renderNotesInput()}
                </Modal.Body>
            </Modal>
            <label
                style={{
                    width: imageContainerSizePx + "px",
                    height: imageContainerHeightSizePx + "px",
                    cursor: isViewOnly ? "default" : "pointer",
                }}
                onClick={handleShow}>
                {currentImageSrc &&
                    getMediaViewer(
                        false,
                        isSmallSizeNoNotes,
                        mediaType,
                        currentImageSrc,
                        imageContainerSizePx,
                        imageContainerHeightSizePx,
                        false,
                        mediaObject,
                        isFullViewer,
                    )
                }
                {!currentImageSrc &&
                    <svg
                        xmlns="http://www.w3.org/2000/svg" fill="currentColor"
                        className="bi bi-image" viewBox="0 0 16 16">
                        <GSPSIcon name={getMediaIconName(mediaType)} className="m-1" />
                    </svg>
                }
            </label>
        </Col>
    </>
    ;
};


GSPSMediaSelector.propTypes = {
    controlSize: PropTypes.number,
    imageContainerSizePx: PropTypes.number,
    imageContainerHeightSizePx: PropTypes.number,
    currentImageSrc: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object,
    ]),
    notes: PropTypes.string,
    mediaType: PropTypes.oneOf([
        "image",
        "video",
    ]),
    elementType: PropTypes.oneOf([
        AnnotationType.ISSUE,
        "area",
        "line",
    ]),
    onNotesSaved: PropTypes.func,
    media: PropTypes.object,
    isViewOnly: PropTypes.bool,
    isSmallSizeNoNotes: PropTypes.bool,
    mediaObject: PropTypes.object,
};
